import React, { useState } from "react";
import { gql, useMutation } from "@apollo/client";
import { useHistory } from "react-router-dom";
import moment from "moment";

// hooks
import { useParams } from "react-router";
import useDishes from "../../common/apollo/hooks/useDishes";
import useUnits from "../../common/apollo/hooks/useUnits";
import useUsers from "../../common/apollo/hooks/useUsers";

// components
import {
  Alert,
  Autocomplete,
  Button,
  ButtonGroup,
  Grid,
  Hidden,
  Snackbar,
  TextField,
} from "@mui/material";
import {
  Add as AddIcon,
  Save as SaveIcon,
  Fastfood as DishesIcon,
} from "@mui/icons-material";

import PlanItem from "./PlanItem";
import DishCreateDialog from "./DishCreateDialog";
import Layout from "../../components/Layout";
import DatePicker from "../../components/DatePicker";
import Loader from "../../common/loader";

const styles = {
  topNav: {
    position: {
      sm: "static",
      xs: "fixed",
    },
    zIndex: {
      sm: 0,
      xs: "99px",
    },
    left: {
      sm: "auto",
      xs: 0,
    },
    top: {
      sm: "auto",
      xs: 0,
    },
    width: {
      sm: "auto",
      xs: "100%",
    },
    padding: {
      sm: 0,
      xs: "12px",
    },
    background: {
      sm: "none",
      xs: "#fff",
    },
    boxShadow: {
      sm: "none",
      xs: "0 0 10px rgba(0, 0, 0, 0.5)",
    },
  },
  topNavActions: {
    "& button": {
      minWidth: { xs: "auto" },
    },
    "& .MuiButton-startIcon": {
      marginRight: { xs: 0 },
    },
  },
  items: {
    paddingTop: {
      sm: "24px",
      xs: "48px",
    },
  },
};

const SAVE_PLAN = gql`
  mutation savePlan($plan: PlanInput) {
    savePlan(plan: $plan)
  }
`;

const createItem = (id, time, meal) => {
  if (!time) time = "00:00";
  if (!meal) meal = "dorucak";

  return {
    id: id,
    time: time,
    meal: meal,
    dishes: [
      {
        id: -1,
        amount: "",
        unit: "",
      },
    ],
  };
};

const DEFAULT_ITEMS = [
  createItem(-1, "08:00", "dorucak"),
  createItem(-2, "11:00", "uzina"),
  createItem(-3, "15:00", "rucak"),
  createItem(-4, "17:00", "uzina"),
  createItem(-5, "20:00", "vecera"),
];

const CreatePlan = () => {
  const { date } = useParams();
  const {
    data: dishes,
    loading: loadingDishes,
    error: errorDishes,
    refetch: reloadDishes,
  } = useDishes();

  const { data: units, loading: loadingUnits, error: errorUnits } = useUnits();

  const { data: users, loading: loadingUsers, error: errorUsers } = useUsers();

  let createDate = moment();
  if (date) {
    const m = moment(date);
    if (m.isValid()) createDate = m;
  }

  const [planUser, setPlanUser] = useState(null);
  const [planUserOpen, setPlanUserOpen] = useState(false);
  const [planDate, setPlanDate] = useState(createDate);
  const [items, setItems] = useState(DEFAULT_ITEMS);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertSeverity, setAlertSeverity] = useState("info");
  const [alertMessage, setAlertMessage] = useState("");
  const [dishesOpen, setDishesOpen] = useState(false);

  const [savePlanMutation, { error: saveError, loading: saveLoading }] =
    useMutation(SAVE_PLAN);

  const history = useHistory();

  if (loadingDishes || loadingUnits || loadingUsers) return <Loader />;
  if (errorDishes || errorUnits || errorUsers) {
    let message = errorDishes?.message;
    if (!message) message = errorUnits?.message;
    if (!message) message = errorUsers?.message;

    setAlertMessage(message || "ERROR");
    setAlertSeverity("error");
    setAlertOpen(true);
  }

  const setItem = (item, prop, value) => {
    setItems(
      items.map((i) => {
        if (item.id === i.id) {
          i[prop] = value;
        }
        return i;
      })
    );
  };

  const addItem = () => {
    const id = -1 * (items.length + 1);
    setItems([...items, createItem(id)]);
  };

  const removeItem = (item) => {
    setItems(
      items.filter((i, idx) => {
        if (i.id === item.id) return false;

        if (i.id < 0) i.id = -1 * idx;
        return true;
      })
    );
  };

  const savePlan = () => {
    const planDateUTC = Date.UTC(
      planDate.year(),
      planDate.month(),
      planDate.date(),
      0,
      0,
      0
    );

    savePlanMutation({
      variables: {
        plan: {
          date: new Date(planDateUTC),
          user: parseInt(planUser?.id) || 0,
          items: items.map((item) => {
            if (item.id) item.id = parseInt(item.id);
            if (item.id < 1) delete item.id;

            item.dishes = item.dishes.map((dish) => {
              if (dish.id) dish.id = parseInt(dish.id);
              if (dish.id)
                if (dish.amount) dish.amount = parseFloat(dish.amount);
              if (!dish.amount) dish.amount = null;

              return dish;
            });

            return item;
          }),
        },
      },
    })
      .then(() => {
        history.push(`/${moment(planDate).format("YYYY-MM-DD")}`);
      })
      .catch((err) => {
        let msg = err.message;
        if (saveError) msg = saveError.message;
        setAlertMessage(`Čuvanje nije uspelo. ${msg}`);
        setAlertSeverity("error");
      })
      .finally(() => setAlertOpen(true));
  };

  const anchor = { vertical: "top", horizontal: "center" };

  return (
    <Layout title="Novi plan">
      {saveLoading && <Loader />}
      {!saveLoading && (
        <>
          <Snackbar
            open={alertOpen}
            autoHideDuration={3000}
            anchorOrigin={anchor}
          >
            <Alert onClose={() => setAlertOpen(false)} severity={alertSeverity}>
              {alertMessage}
            </Alert>
          </Snackbar>
          <DishCreateDialog
            open={dishesOpen}
            onClose={() => setDishesOpen(false)}
            reloadDishes={reloadDishes}
          />
          <Grid container sx={styles.topNav}>
            <Grid item xs={12} sx={styles.topNavActions}>
              <ButtonGroup>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => addItem()}
                >
                  <Hidden xsDown>Dodaj obrok</Hidden>
                </Button>
                <Button
                  variant="contained"
                  startIcon={<DishesIcon />}
                  onClick={() => setDishesOpen(true)}
                >
                  <Hidden xsDown>Novo jelo</Hidden>
                </Button>
              </ButtonGroup>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<SaveIcon />}
                style={{ float: "right" }}
                onClick={() => savePlan()}
              >
                <Hidden xsDown>Sačuvaj plan</Hidden>
              </Button>
            </Grid>
          </Grid>
          <Grid container sx={styles.items}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                variant="inline"
                label="Datum:"
                date={planDate}
                format="ddd, DD.MM.yyyy"
                onChange={(date) => setPlanDate(date)}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Autocomplete
                open={planUserOpen}
                value={planUser}
                options={users}
                onOpen={() => setPlanUserOpen(true)}
                onClose={() => setPlanUserOpen(false)}
                onChange={(_, newVal) => {
                  setPlanUser(newVal);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Korisnik"
                    variant="standard"
                    InputProps={{ ...params.InputProps }}
                  />
                )}
                isOptionEqualToValue={(option, value) => {
                  return parseInt(option.id) === parseInt(value.id);
                }}
                getOptionLabel={(option) => {
                  const opt = users.find((user) => {
                    return parseInt(user.id) === parseInt(option.id);
                  });
                  if (!opt) return "";

                  if (opt.name === opt.email) return opt.name;

                  return `${opt.name} (${opt.email})`;
                }}
                disableClearable
              />
            </Grid>
          </Grid>
          {items.map((item, index) => {
            return (
              <PlanItem
                key={`plan-item-${index}`}
                item={item}
                dishes={dishes}
                units={units}
                reloadDishes={reloadDishes}
                onRemove={(item) => removeItem(item)}
                onSet={(item, prop, val) => setItem(item, prop, val)}
              />
            );
          })}
        </>
      )}
    </Layout>
  );
};

export default CreatePlan;
